import { NumberSeparator } from '@databases/model/custom-number-format';
import {
  BacklinkDisplayType,
  type FieldJSON,
  FieldType,
  NumberFormat,
  PageSubType,
  ParentPageOption,
  ParentPageType,
  MediaFormat
} from '@databases/model/field';
import {
  CollabCardWidth,
  CollabCoverFieldType,
  CollabLayoutType,
  type DatabaseViewJSON
} from '@databases/model/views/view';
import type * as Y from 'yjs';

import { SynchronizedArray } from './synchronized-array';
import { type BasicYArray } from './types';
import { YUniqueArray } from './y-unique-array';

export { v4 as generateId } from 'uuid';

/**
 * Moves the array item at the given position to the new position. If the array item does not exist
 * (idx >= array.length), nothing is done.
 * @param toIdx The element will be inserted at this index. If toIdx < fromIdx, all following elements
 *   will be pushed infront it. If toIdx > fromIdx, all following elements will be pushed behind it.
 *   This behavior makes the most sense for this functions use case, which is drag and drop select items.
 * @param clone Will be called to clone the element before moving it. This needs to be defined if the
 *   array elements are native Y types, since those cannot be reattached once removed from the doc.
 */
export function moveArrayItem<T>(
  array: BasicYArray<T>,
  fromIdx: number,
  toIdx: number,
  clone: (value: T) => T = (value) => value
): void {
  if (!array.doc) {
    throw new Error('Array must be attached to a doc.');
  }

  array.doc.transact(() => {
    if (fromIdx < array.length) {
      const val = clone(array.get(fromIdx));
      array.delete(fromIdx);
      array.insert(toIdx, [val]);
    }
  });
}

export const createUniqueStringArray = (arr: Y.Array<any>): YUniqueArray<string> => {
  return new YUniqueArray<string>(
    new SynchronizedArray(arr, (el) => (typeof el === 'string' ? el : undefined))
  );
};

declare module 'yjs' {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface AbstractType<EventType> {
    _databases_integrateHandlers?: Set<() => void>;
  }
}

/**
 * Registers a handler that is called when the given Y type is integrated into a document.
 */
export function registerIntegrateHandler(
  obj: Y.AbstractType<any>,
  handler: () => void
): void {
  if (!obj._databases_integrateHandlers) {
    obj._databases_integrateHandlers = new Set();
    const integrateBkp = obj._integrate;
    obj._integrate = function _integrate(doc: Y.Doc, item: Y.Item) {
      integrateBkp.call(this, doc, item);
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      for (const handler of this._databases_integrateHandlers!) {
        handler();
      }
    };
  }

  obj._databases_integrateHandlers.add(handler);
}

/**
 * Unregisters a handler registered through registerIntegrateHandler().
 */
export function unregisterIntegrateHandler(
  obj: Y.AbstractType<any>,
  handler: () => void
): void {
  if (obj._databases_integrateHandlers) {
    obj._databases_integrateHandlers.delete(handler);
  }
}

export const DEFAULT_FIELD_SETTINGS: Omit<
  FieldJSON,
  'name' | 'description' | 'width' | 'type'
> = {
  allowEdit: true,
  backlinkDisplayType: BacklinkDisplayType.AS_NUMBER,
  cloudId: undefined,
  contentField: undefined,
  creatable: undefined,
  customNumberFormat: {
    decimalSeparator: NumberSeparator.DOT,
    minFractionDigits: undefined,
    maxFractionDigits: undefined,
    decimalPlaces: undefined,
    thousandsSeparator: undefined,
    prefix: undefined,
    suffix: undefined
  },
  databaseId: undefined,
  defaultEnabled: false,
  displayEntryOnPage: undefined,
  displayedDetail: undefined,
  entryFieldField: undefined,
  fieldId: undefined,
  includeVersionComment: undefined,
  isParentPageSourceDatabase: undefined,
  jiraField: undefined,
  jiraServerId: undefined,
  mentionNotificationEnabled: true,
  multi: true,
  numberFormat: NumberFormat.NUMBER,
  mediaFormat: MediaFormat.AUTOFILL,
  pageTemplateId: undefined,
  pageSubType: PageSubType.LIVE,
  parentPage: undefined,
  parentPageOption: ParentPageOption.FIXED,
  parentPageType: ParentPageType.PAGE,
  resolve: true,
  selectOptions: [],
  showDescription: false,
  dateFormat: undefined,
  timeFormat: undefined,
  enableDateRange: undefined,
  aiFieldConfig: undefined,
  aiRunMetadata: undefined
};

export function createDefaultFieldJSON(name: string, type: FieldType): FieldJSON {
  return {
    description: '',
    name,
    type,
    width: undefined,
    ...DEFAULT_FIELD_SETTINGS,
    ...(type === FieldType.PageLink ? { creatable: true } : {})
  };
}

export const DEFAULT_VIEW: Omit<DatabaseViewJSON, 'name'> = {
  calculations: {},
  cardWidth: CollabCardWidth.MEDIUM,
  coverField: { type: CollabCoverFieldType.Default },
  showFieldNames: true,
  dateFieldId: undefined,
  showNumberedEntries: true,
  fields: undefined,
  filter: undefined,
  grouping: undefined,
  layoutType: CollabLayoutType.TABLE,
  sort: undefined,
  sorts: []
};
